import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React, { useState } from "react";
import { useAppSelector } from "../../app/hooks";
import {
  SubscriptionListItem,
  useCancelTenantSubscriptionMutation,
  useGetSubscriptionsListQuery,
} from "../../api/subscriptionApi";
import { ConfirmationDialog } from "../../components/common/ConfirmationDialog/ConfirmationDialog";
import { DataTable, Column, Action } from "../../components/common/DataTable/DataTable";
import PageTitle from "../../components/common/PageTitle";
import { PaymentIcon } from "../../components/common/pageIcons";
import PageLayout from "../../components/layout/PageLayout";
import { useToast } from "../../contexts/ToastContext";
import { extractErrorMessage } from "../../utils/errorUtils";

const subscriptionChipColor = (
  status: string | undefined | null
): "success" | "info" | "warning" | "error" | "default" => {
  switch ((status || "").toLowerCase()) {
    case "active":
      return "success";
    case "trialing":
      return "info";
    case "past_due":
      return "warning";
    case "canceled":
      return "default";
    default:
      return "default";
  }
};

/** Plan + billing state, e.g. "Pro · Trial" or "Lite · Paid". */
const formatPlanSubscription = (row: SubscriptionListItem) => {
  const plan = row.planName || "Unknown plan";
  const status = (row.status || "").toLowerCase();

  if (status === "trialing") return `${plan} · Trial`;
  if (status === "active") return `${plan} · Paid`;
  if (status === "past_due") return `${plan} · Past due`;
  if (status === "canceled") return `${plan} · Canceled`;
  if (status) return `${plan} · ${status.replace(/_/g, " ")}`;
  return plan;
};

const SubscriptionsManagementPage: React.FC = () => {
  const {
    data: subscriptions,
    isLoading,
    error,
  } = useGetSubscriptionsListQuery();
  const user = useAppSelector((state) => state.auth.user);
  const isSuperAdmin = user?.role === "SuperAdmin";
  const { showError, showSuccess } = useToast();

  const [selected, setSelected] = useState<SubscriptionListItem | null>(null);
  const [cancelTenantSubscription, { isLoading: isCanceling }] =
    useCancelTenantSubscriptionMutation();

  const columns: Column[] = [
    {
      field: "tenantName",
      headerName: isSuperAdmin ? "Tenant" : "Company",
      width: 200,
      renderCell: (_value: any, row: SubscriptionListItem) =>
        row?.tenantName ?? "Unknown",
    },
    {
      field: "planName",
      headerName: "Subscription",
      width: 200,
      renderCell: (_value: any, row: SubscriptionListItem) => (
        <Chip
          label={formatPlanSubscription(row)}
          color={subscriptionChipColor(row?.status)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "startDate",
      headerName: "Starts",
      width: 150,
      renderCell: (_value: any, row: SubscriptionListItem) =>
        row?.startDate
          ? format(new Date(row.startDate), "MMM dd, yyyy")
          : "N/A",
    },
    {
      field: "vehicleQuantity",
      headerName: "Vehicle Quantity",
      width: 140,
      renderCell: (_value: any, row: SubscriptionListItem) => (
        <Typography variant="body2">{row?.vehicleQuantity ?? 0}</Typography>
      ),
    },
    {
      field: "syncedVehicleCount",
      headerName: "Synced Vehicles",
      width: 140,
      renderCell: (_value: any, row: SubscriptionListItem) => {
        const vehicleQuantity = row?.vehicleQuantity ?? 0;
        const syncedCount = row?.syncedVehicleCount ?? 0;
        const isComplete = syncedCount >= vehicleQuantity;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: isComplete ? "success.main" : "warning.main",
                fontWeight: isComplete ? 600 : 400,
              }}
            >
              {syncedCount}
            </Typography>
            {isComplete ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
            ) : (
              <ScheduleIcon sx={{ fontSize: 16, color: "warning.main" }} />
            )}
          </Box>
        );
      },
    },
  ];

  const getActions = (row: SubscriptionListItem): Action<SubscriptionListItem>[] => {
    if (!isSuperAdmin) return [];

    const canCancel =
      row.status === "active" || row.status === "trialing";

    if (!canCancel) return [];

    return [
      {
        icon: <CancelIcon />,
        label: "Cancel Subscription",
        onClick: (item) => setSelected(item),
        color: "warning",
      },
    ];
  };

  const handleCancelConfirm = async () => {
    if (!selected) return;
    try {
      const result = await cancelTenantSubscription({
        tenantId: selected.tenantId,
        immediately: true,
      }).unwrap();
      showSuccess(result.message || "Subscription canceled");
      setSelected(null);
    } catch (err) {
      showError(extractErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Alert severity="error">
          Failed to load subscriptions. Please try again later.
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageTitle
        title={isSuperAdmin ? "All Subscriptions" : "Subscription"}
        icon={<PaymentIcon />}
        titleVariant="h4"
        subtitle={
          isSuperAdmin
            ? "View and manage subscriptions across all tenants"
            : "View your subscription details and vehicle sync status"
        }
      />

      {!subscriptions || subscriptions.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No active subscriptions found.
          </Typography>
        </Paper>
      ) : (
        <DataTable<SubscriptionListItem>
          items={subscriptions}
          columns={columns}
          actions={isSuperAdmin ? getActions : undefined}
          isLoading={isLoading}
          title={isSuperAdmin ? "All Subscriptions" : "Subscription Details"}
        />
      )}

      <ConfirmationDialog
        open={Boolean(selected)}
        title="Cancel Subscription"
        message={`Cancel the subscription for "${selected?.tenantName}" immediately? Billing will stop and the tenant will lose paid access.`}
        confirmLabel="Cancel Subscription"
        cancelLabel="Keep Subscription"
        confirmColor="warning"
        onClose={() => setSelected(null)}
        onConfirm={handleCancelConfirm}
        isLoading={isCanceling}
      />
    </PageLayout>
  );
};

export default SubscriptionsManagementPage;
